import React, { useState } from 'react';
import styled from 'styled-components';
import { authService } from '../../services/auth';

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 1rem;
`;

const Button = styled.button`
  padding: 0.75rem;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  cursor: pointer;

  &:hover {
    background-color: #0056b3;
  }

  &:disabled {
    background-color: #6c757d;
    cursor: not-allowed;
  }
`;

const SecondaryButton = styled(Button)`
  background-color: #6c757d;

  &:hover {
    background-color: #545b62;
  }
`;

const ForgotPasswordButton = styled.button`
  background: none;
  border: none;
  color: #007bff;
  text-decoration: underline;
  cursor: pointer;
  font-size: 0.875rem;
  padding: 0;
  margin-top: 0.5rem;

  &:hover {
    color: #0056b3;
  }
`;

const ErrorMessage = styled.div`
  color: #dc3545;
  font-size: 0.875rem;
`;

const SuccessMessage = styled.div`
  color: #28a745;
  font-size: 0.875rem;
`;

const HelpText = styled.div`
  color: #6c757d;
  font-size: 0.875rem;
  margin-top: -0.5rem;
  margin-bottom: 0.5rem;
`;

interface EmailSignInFormProps {
  onSuccess: () => void;
  onError: (error: string) => void;
  onModeChange?: (mode: 'signin' | 'signup' | 'forgot') => void;
}

const EmailSignInForm: React.FC<EmailSignInFormProps> = ({ onSuccess, onError, onModeChange }) => {
  const [isRegistering, setIsRegistering] = useState(false);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsLoading(true);

    try {
      if (isForgotPassword) {
        // Handle forgot password
        const result = await authService.forgotPassword(email);
        setSuccess(result.message);
      } else if (isRegistering) {
        // Handle signup (name + email only)
        const result = await authService.signup(email, name);
        setSuccess(result.message);
      } else {
        // Handle sign in
        await authService.login(email, password);
        onSuccess();
      }
    } catch (error: any) {
      const message = error.response?.data?.error || error.message || 'An error occurred';
      setError(message);
      onError(message);
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setEmail('');
    setPassword('');
    setName('');
    setError('');
    setSuccess('');
    setIsLoading(false);
  };

  const handleModeChange = (newMode: 'signin' | 'signup' | 'forgot') => {
    resetForm();
    setIsRegistering(newMode === 'signup');
    setIsForgotPassword(newMode === 'forgot');
    // Notify parent component about mode change
    onModeChange?.(newMode);
  };

  if (isForgotPassword) {
    return (
      <Form onSubmit={handleSubmit}>
        <h3 style={{ color: 'white', margin: '0 0 1rem 0' }}>Reset Password</h3>
        {success ? (
          <>
            <SuccessMessage>{success}</SuccessMessage>
            <SecondaryButton
              type="button"
              onClick={() => handleModeChange('signin')}
            >
              Back to Sign In
            </SecondaryButton>
          </>
        ) : (
          <>
            <Input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={isLoading}
            />
            {error && <ErrorMessage>{error}</ErrorMessage>}
            <Button type="submit" disabled={isLoading}>
              {isLoading ? 'Sending...' : 'Send Reset Link'}
            </Button>
            <SecondaryButton
              type="button"
              onClick={() => handleModeChange('signin')}
              disabled={isLoading}
            >
              Back to Sign In
            </SecondaryButton>
          </>
        )}
      </Form>
    );
  }

  return (
    <Form onSubmit={handleSubmit}>
      {success && isRegistering ? (
        <SuccessMessage>{success}</SuccessMessage>
      ) : (
        <>
          {isRegistering && (
            <Input
              type="text"
              placeholder="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              disabled={isLoading}
            />
          )}
          <Input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={isLoading}
          />
          {isRegistering && (
            <HelpText>Login link will be sent to this email address</HelpText>
          )}
          {!isRegistering && (
            <Input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={isLoading}
            />
          )}
          {error && <ErrorMessage>{error}</ErrorMessage>}
          <Button type="submit" disabled={isLoading}>
            {isLoading 
              ? (isRegistering ? 'Creating Account...' : 'Signing In...') 
              : (isRegistering ? 'Create Account' : 'Sign In')
            }
          </Button>
          <SecondaryButton
            type="button"
            onClick={() => handleModeChange(isRegistering ? 'signin' : 'signup')}
            disabled={isLoading}
          >
            {isRegistering ? 'Already have an account? Sign In' : 'Need an account? Sign Up'}
          </SecondaryButton>
          {!isRegistering && (
            <ForgotPasswordButton
              type="button"
              onClick={() => handleModeChange('forgot')}
              disabled={isLoading}
            >
              Forgot Password?
            </ForgotPasswordButton>
          )}
        </>
      )}
    </Form>
  );
};

export default EmailSignInForm; 